import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import callOrLog from 'common/utils/callOrLog';
import { SEARCH } from 'common/utils/EventTracker/utils/eventConstants';
import { viewable } from 'common/utils/Permission/Permission';
import {
  getItemResult,
  goToSearchResult,
} from '../../utils';
import {
  goToSearchIndex,
  getSearchBarColumnHeaders,
} from '../utils';

const SearchBarTableRow = ({
  index, model, result, navigateToRow, rowRef, scope,
}, context) => {
  const {
    closeSearchResults,
    eventTracker,
    query,
    clientIdStandardizationEnabled,
    medicaidFilteringEnabled,
  } = context;
  const keyId = `${model}-${index}`;
  const columnRefs = useRef([]);

  const tableConfig = getSearchBarColumnHeaders(
    model,
    clientIdStandardizationEnabled,
    medicaidFilteringEnabled,
  );

  const { headers, getRowValues } = tableConfig;
  const columnsKeys = map(headers, 'value');

  const onRowClick = () => {
    const resultItem = getItemResult(result);

    closeSearchResults();
    if (viewable(resultItem)) {
      goToSearchResult(model, result);
    } else {
      goToSearchIndex({ query, scope });
    }

    callOrLog(() => eventTracker(SEARCH.clickResult, { model }));
  };

  const navigateToColumn = (columnIndex) => {
    const column = columnRefs.current[columnIndex];
    if (column) column.focus();
  };

  const handleKeyDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const currentColumnIndex = e.target.tagName === 'TD' ? e.target.cellIndex : 0;
    const totalColumns = columnsKeys.length;
    switch (e.key) {
      case 'ArrowDown':
      case 'Tab':
        navigateToRow(index + 1);
        break;
      case 'ArrowUp':
        navigateToRow(index - 1);
        break;
      case 'ArrowRight':
        if (currentColumnIndex < totalColumns - 1) navigateToColumn(currentColumnIndex + 1);
        break;
      case 'ArrowLeft':
        if (currentColumnIndex > 0) navigateToColumn(currentColumnIndex - 1);
        if (currentColumnIndex === 0) navigateToColumn(currentColumnIndex);
        break;
      case ' ':
      case 'Enter':
        onRowClick();
        break;
      default:
        break;
    }
  };

  const getColumnAriaText = (columnIndex, value) => {
    const headerAriaLabel = headers[columnIndex]?.ariaLabel || `column ${columnIndex + 1}`;
    return value ? `${headerAriaLabel} ${value}` : headerAriaLabel;
  };

  const rowValues = getRowValues(
    {
      result,
      columns: columnsKeys,
      clientIdStandardizationEnabled,
      medicaidFilteringEnabled,
    },
  );

  const columns = rowValues.map((column, i) => (
    <td
      key={`${keyId}-${column.label}`}
      ref={(element) => { columnRefs.current[i] = element; }}
      className="search-result__column"
      aria-label={getColumnAriaText(i, column.value)}
      role="gridcell"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <span aria-hidden="true">{column.value}</span>
    </td>
  ));

  const r0Name = getColumnAriaText(0, rowValues[0].value);
  const r1ExtID = rowValues[4]?.value ? `, ${getColumnAriaText(4, rowValues[4].value)}` : '';
  const r2Phone = rowValues[1]?.value ? `, ${getColumnAriaText(1, rowValues[1].value)}` : '';
  const r3DOB = rowValues[2]?.value ? `, ${getColumnAriaText(2, rowValues[2].value)}` : '';
  const r4Loc = rowValues[3]?.value ? `, ${getColumnAriaText(3, rowValues[3].value)}` : '';
  const accessibilityLabel = r0Name + r1ExtID + r2Phone + r3DOB + r4Loc;

  return (
    <tr
      ref={rowRef}
      className="search-result"
      id={keyId}
      role="row"
      onClick={onRowClick}
      onKeyDown={handleKeyDown}
      aria-label={accessibilityLabel}
      aria-describedby={`search-result-description-${model}`}
      tabIndex={index}
    >
      {columns}
    </tr>
  );
};

SearchBarTableRow.propTypes = {
  index: PropTypes.number.isRequired,
  model: PropTypes.string.isRequired,
  result: PropTypes.object.isRequired,
  navigateToRow: PropTypes.func.isRequired,
  rowRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  scope: PropTypes.string.isRequired,
};

SearchBarTableRow.contextTypes = {
  clientIdStandardizationEnabled: PropTypes.bool.isRequired,
  medicaidFilteringEnabled: PropTypes.bool.isRequired,
  closeSearchResults: PropTypes.func.isRequired,
  eventTracker: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
};

export default SearchBarTableRow;
